import { AntragHelperService, AntragStatus, EndToEndEncrypter, Guid } from '@ebeihilfe/core';
import { AntragMetaData, FormResult, FormValue, LegacyAntragsMetadata, PartiallyTypedNewAntrag } from '../model';
import { AntragMetaDataDtoAssemblerService } from '../dto-assembler/antrag-meta-data-dto-assembler.service';
import { AntragUpdateDtoAssemblerService } from '../dto-assembler/antrag-update-dto.assembler.service';
import { EinreichungApiService } from './einreichung-api.service';
import { Injectable, inject } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class EinreichungService {
  private readonly antragHelperService = inject(AntragHelperService);
  private readonly antragMetaDataDtoAssemblerService = inject(AntragMetaDataDtoAssemblerService);
  private readonly antragUpdateDtoAssemblerService = inject(AntragUpdateDtoAssemblerService);
  private readonly einreichungApiService = inject(EinreichungApiService);
  private readonly endToEndEncrypter = inject(EndToEndEncrypter);

  // should be on Antrag object, but is needed on "application"
  public isAntragCompletelyTransferred(status: AntragStatus): boolean {
    return !this.antragHelperService.isDraftAntrag(status);
  }

  public isAntragInDraftMode(status: AntragStatus): boolean {
    return this.antragHelperService.isDraftAntrag(status);
  }

  public updateAntrag(partialAntrag: PartiallyTypedNewAntrag): Observable<{ concurrency: Date }> {
    return from(this.antragUpdateDtoAssemblerService.toDto(partialAntrag)).pipe(
      switchMap((antragUpdateDto) => this.einreichungApiService.updateAntrag(antragUpdateDto)),
    );
  }

  public async encryptAntragFinishMetadata(metadata: AntragMetaData | LegacyAntragsMetadata[]): Promise<string> {
    if (metadata instanceof Array) {
      metadata = this.convertLegacyAntragMetadataToAntragsMetadata(metadata);
    }

    const metadataDto = this.antragMetaDataDtoAssemblerService.toDto(metadata);

    const base64EncryptedMetadata = await this.endToEndEncrypter.encryptToBase64(JSON.stringify(metadataDto));

    if (base64EncryptedMetadata == null) {
      throw new Error('Could not encrypt metadata');
    }

    return base64EncryptedMetadata;
  }

  private convertLegacyAntragMetadataToAntragsMetadata(data: LegacyAntragsMetadata[]): AntragMetaData {
    return new AntragMetaData({
      formResults: [
        new FormResult({
          results: data.map((datum) => {
            return FormValue.createFromLegacy(datum.keyInternal, datum.type, datum.value);
          }),
          isValid: true,
          formId: Guid.create(),
          templateName: '::LEGACY::',
          isMandatory: true,
          createdAt: new Date(),
        }),
      ],
    });
  }
}
